import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useToast } from '../App';
import LoadingState from '../components/LoadingState';

const OrderDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [noteType, setNoteType] = useState('private');
    const [orderNotes, setOrderNotes] = useState([]);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editNoteContent, setEditNoteContent] = useState('');
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');

    const orderStatuses = [
        { value: 'pending', label: 'Pending Payment', color: '#f59e0b', bg: '#fef3c7' },
        { value: 'processing', label: 'Processing', color: '#3b82f6', bg: '#dbeafe' },
        { value: 'on-hold', label: 'On Hold', color: '#6b7280', bg: '#f3f4f6' },
        { value: 'completed', label: 'Completed', color: '#10b981', bg: '#d1fae5' },
        { value: 'cancelled', label: 'Cancelled', color: '#ef4444', bg: '#fee2e2' },
        { value: 'refunded', label: 'Refunded', color: '#8b5cf6', bg: '#ede9fe' },
        { value: 'failed', label: 'Failed', color: '#dc2626', bg: '#fee2e2' }
    ];

    useEffect(() => {
        loadOrder();
    }, [id]);

    const loadOrder = async () => {
        try {
            setLoading(true);
            const [orderResponse, notesResponse] = await Promise.all([
                api.getOrder(id),
                api.getOrderNotes(id)
            ]);

            if (orderResponse.success) {
                setOrder(orderResponse.order);
            } else {
                throw new Error(orderResponse.message || 'Failed to load order');
            }

            if (notesResponse.success) {
                setOrderNotes(notesResponse.notes || []);
            }
        } catch (err) {
            showError(err.message);
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === order.status) return;

        try {
            setUpdating(true);
            const response = await api.updateOrder(order.id, { status: newStatus });

            if (response.success) {
                showSuccess('Order status updated successfully');
                await loadOrder();
            } else {
                throw new Error(response.message || 'Failed to update status');
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setUpdating(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) {
            showError('Please enter a note');
            return;
        }

        try {
            const response = await api.addOrderNote(id, newNote, noteType);

            if (response.success) {
                showSuccess('Note added successfully');
                setNewNote('');
                // Reload notes
                const notesResponse = await api.getOrderNotes(id);
                if (notesResponse.success) {
                    setOrderNotes(notesResponse.notes || []);
                }
            } else {
                throw new Error(response.message || 'Failed to add note');
            }
        } catch (err) {
            showError(err.message);
        }
    };

    const handleEditNote = (note) => {
        setEditingNoteId(note.id);
        setEditNoteContent(note.content);
    };

    const handleSaveEditNote = async (noteId) => {
        try {
            const response = await api.updateOrderNote(id, noteId, editNoteContent);

            if (response.success) {
                showSuccess('Note updated successfully');
                setEditingNoteId(null);
                setEditNoteContent('');
                // Reload notes
                const notesResponse = await api.getOrderNotes(id);
                if (notesResponse.success) {
                    setOrderNotes(notesResponse.notes || []);
                }
            } else {
                throw new Error(response.message || 'Failed to update note');
            }
        } catch (err) {
            showError(err.message);
        }
    };

    const handleDeleteNote = async (noteId) => {
        if (!window.confirm('Are you sure you want to delete this note?')) {
            return;
        }

        try {
            const response = await api.deleteOrderNote(id, noteId);

            if (response.success) {
                showSuccess('Note deleted successfully');
                // Reload notes
                const notesResponse = await api.getOrderNotes(id);
                if (notesResponse.success) {
                    setOrderNotes(notesResponse.notes || []);
                }
            } else {
                throw new Error(response.message || 'Failed to delete note');
            }
        } catch (err) {
            showError(err.message);
        }
    };

    const handleRefund = async () => {
        if (!refundAmount || parseFloat(refundAmount) <= 0) {
            showError('Please enter a valid refund amount');
            return;
        }

        if (parseFloat(refundAmount) > parseFloat(order.total)) {
            showError('Refund amount cannot exceed order total');
            return;
        }

        try {
            setUpdating(true);
            const response = await api.refundOrder(id, refundAmount, refundReason || 'Refund processed from admin');

            if (response.success) {
                showSuccess(`Refund of ${formatCurrency(refundAmount, order.currency)} processed successfully`);
                setRefundDialogOpen(false);
                setRefundAmount('');
                setRefundReason('');
                await loadOrder();
            } else {
                throw new Error(response.message || 'Failed to process refund');
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setUpdating(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusInfo = (status) => {
        return orderStatuses.find(s => s.value === status) || orderStatuses[0];
    };

    if (loading) {
        return (
            <div className="page order-details-page">
                <div className="container container-xl">
                    <LoadingState message="Loading order details..." />
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="page order-details-page">
                <div className="container container-xl">
                    <div className="empty-state">
                        <p>Order not found</p>
                        <button className="btn btn-primary" onClick={() => navigate('/orders')}>
                            Back to Orders
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const statusInfo = getStatusInfo(order.status);
    const subtotal = parseFloat(order.total) - parseFloat(order.shipping_total || 0) - parseFloat(order.total_tax || 0);

    return (
        <div className="page order-details-page">
            <div className="container container-xl">
                {/* Header */}
                <div className="order-header">
                    <button className="btn-back" onClick={() => navigate('/orders')}>
                        ← Back to Orders
                    </button>
                    <div className="order-header-content">
                        <div className="order-title-section">
                            <h1>Order #{order.number || order.id}</h1>
                            <div className="order-meta">
                                <span className="order-date">📅 {formatDate(order.date_created)}</span>
                                <span
                                    className="order-status-badge"
                                    style={{
                                        background: statusInfo.bg,
                                        color: statusInfo.color
                                    }}
                                >
                                    {statusInfo.label}
                                </span>
                            </div>
                        </div>
                        <div className="order-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => navigate(`/orders/edit/${order.id}`)}
                            >
                                ✏️ Edit Order
                            </button>
                        </div>
                    </div>
                </div>

                <div className="order-layout">
                    {/* Left Column - Main Content */}
                    <div className="order-main">
                        {/* Order Items */}
                        <div className="card order-items-card">
                            <div className="card-header">
                                <h3>Order Items</h3>
                            </div>
                            <div className="card-content">
                                <table className="order-items-table">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th className="text-center">Qty</th>
                                            <th className="text-right">Price</th>
                                            <th className="text-right">Line Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.line_items && order.line_items.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="product-cell">
                                                        <div className="product-image">
                                                            {item.image ? (
                                                                <img src={typeof item.image === 'string' ? item.image : item.image.src} alt={item.name} />
                                                            ) : (
                                                                <div className="no-image">📦</div>
                                                            )}
                                                        </div>
                                                        <div className="product-info">
                                                            <div className="product-name">{item.name}</div>
                                                            {item.meta_data && item.meta_data.length > 0 && (
                                                                <div className="product-meta">
                                                                    {item.meta_data.map((meta, i) => (
                                                                        <span key={i} className="meta-item">
                                                                            {meta.display_key}: {meta.display_value}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="sku-cell">{item.sku || '—'}</td>
                                                <td className="text-center qty-cell">{item.quantity}</td>
                                                <td className="text-right price-cell">{formatCurrency(item.price, order.currency)}</td>
                                                <td className="text-right total-cell">{formatCurrency(item.total, order.currency)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="subtotal-row">
                                            <td colSpan="4" className="text-right">Subtotal:</td>
                                            <td className="text-right">{formatCurrency(subtotal, order.currency)}</td>
                                        </tr>
                                        {parseFloat(order.discount_total || 0) > 0 && (
                                            <tr className="discount-row">
                                                <td colSpan="4" className="text-right">Discount:</td>
                                                <td className="text-right text-success">-{formatCurrency(order.discount_total, order.currency)}</td>
                                            </tr>
                                        )}
                                        {parseFloat(order.shipping_total || 0) > 0 && (
                                            <tr className="shipping-row">
                                                <td colSpan="4" className="text-right">
                                                    Shipping
                                                    {order.shipping_lines && order.shipping_lines.length > 0 && (
                                                        <span className="shipping-method"> ({order.shipping_lines[0].method_title})</span>
                                                    )}:
                                                </td>
                                                <td className="text-right">{formatCurrency(order.shipping_total, order.currency)}</td>
                                            </tr>
                                        )}
                                        {parseFloat(order.total_tax || 0) > 0 && (
                                            <tr className="tax-row">
                                                <td colSpan="4" className="text-right">Tax:</td>
                                                <td className="text-right">{formatCurrency(order.total_tax, order.currency)}</td>
                                            </tr>
                                        )}
                                        <tr className="total-row">
                                            <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                                            <td className="text-right"><strong>{formatCurrency(order.total, order.currency)}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                                <div className="card-footer">
                                    <button
                                        className="btn btn-danger"
                                        onClick={() => {
                                            setRefundAmount(order.total);
                                            setRefundDialogOpen(true);
                                        }}
                                        disabled={order.status === 'refunded'}
                                    >
                                        💸 Process Refund
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Addresses */}
                        <div className="addresses-grid">
                            {/* Shipping Address */}
                            <div className="card">
                                <div className="card-header">
                                    <h3>🚚 Shipping Address</h3>
                                </div>
                                <div className="card-content">
                                    {order.shipping ? (
                                        <div className="address-block">
                                            <div className="address-name">{order.shipping.first_name} {order.shipping.last_name}</div>
                                            {order.shipping.company && <div>{order.shipping.company}</div>}
                                            <div>{order.shipping.address_1}</div>
                                            {order.shipping.address_2 && <div>{order.shipping.address_2}</div>}
                                            <div>{order.shipping.city}, {order.shipping.state} {order.shipping.postcode}</div>
                                            <div>{order.shipping.country}</div>
                                        </div>
                                    ) : (
                                        <p className="text-muted">No shipping address</p>
                                    )}
                                </div>
                            </div>

                            {/* Billing Address */}
                            <div className="card">
                                <div className="card-header">
                                    <h3>💳 Billing Address</h3>
                                </div>
                                <div className="card-content">
                                    {order.billing ? (
                                        <div className="address-block">
                                            <div className="address-name">{order.billing.first_name} {order.billing.last_name}</div>
                                            {order.billing.company && <div>{order.billing.company}</div>}
                                            <div>{order.billing.address_1}</div>
                                            {order.billing.address_2 && <div>{order.billing.address_2}</div>}
                                            <div>{order.billing.city}, {order.billing.state} {order.billing.postcode}</div>
                                            <div>{order.billing.country}</div>
                                            {order.billing.email && <div className="contact-info">📧 {order.billing.email}</div>}
                                            {order.billing.phone && <div className="contact-info">📞 {order.billing.phone}</div>}
                                        </div>
                                    ) : (
                                        <p className="text-muted">No billing address</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Payment Details */}
                        <div className="card">
                            <div className="card-header">
                                <h3>💰 Payment Details</h3>
                            </div>
                            <div className="card-content">
                                <div className="payment-details">
                                    <div className="detail-row">
                                        <span className="detail-label">Payment Status:</span>
                                        <span className="detail-value">
                                            <span className={`status-badge ${order.date_paid ? 'paid' : 'unpaid'}`}>
                                                {order.date_paid ? '✓ Paid' : '⏳ Unpaid'}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Payment Method:</span>
                                        <span className="detail-value">{order.payment_method_title || 'N/A'}</span>
                                    </div>
                                    {order.date_paid && (
                                        <div className="detail-row">
                                            <span className="detail-label">Date Paid:</span>
                                            <span className="detail-value">{formatDate(order.date_paid)}</span>
                                        </div>
                                    )}
                                    {order.transaction_id && (
                                        <div className="detail-row">
                                            <span className="detail-label">Transaction ID:</span>
                                            <span className="detail-value">{order.transaction_id}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Custom Fields from Order Items */}
                        {(() => {
                            // Check if any order items have custom fields (meta_data)
                            const itemsWithCustomFields = order.line_items?.filter(item => {
                                if (!item.meta_data) return false;
                                // meta_data can be an array or an object
                                if (Array.isArray(item.meta_data)) {
                                    return item.meta_data.length > 0;
                                }
                                // If it's an object, check if it has keys
                                return Object.keys(item.meta_data).length > 0;
                            }) || [];
                            
                            if (itemsWithCustomFields.length === 0) return null;
                            
                            return (
                                <div className="card">
                                    <div className="card-header">
                                        <h3>📋 Custom Product Fields</h3>
                                    </div>
                                    <div className="card-content">
                                        {itemsWithCustomFields.map((item, index) => {
                                            // Convert meta_data to array if it's an object
                                            let customFields = [];
                                            if (Array.isArray(item.meta_data)) {
                                                customFields = item.meta_data;
                                            } else if (typeof item.meta_data === 'object') {
                                                customFields = Object.values(item.meta_data);
                                            }
                                            
                                            if (customFields.length === 0) return null;
                                            
                                            return (
                                                <div key={index} style={{ 
                                                    marginBottom: itemsWithCustomFields.length > 1 ? '20px' : '0', 
                                                    paddingBottom: index < itemsWithCustomFields.length - 1 ? '15px' : '0', 
                                                    borderBottom: index < itemsWithCustomFields.length - 1 ? '1px solid #e5e7eb' : 'none' 
                                                }}>
                                                    <div style={{ fontWeight: '600', marginBottom: '10px', color: '#374151' }}>
                                                        {item.name}
                                                    </div>
                                                    <div className="payment-details">
                                                        {customFields.map((meta, i) => {
                                                            // Strip HTML tags from display_value
                                                            const displayValue = meta.display_value 
                                                                ? meta.display_value.replace(/<[^>]*>/g, '').trim()
                                                                : meta.value;
                                                            
                                                            return (
                                                                <div key={i} className="detail-row">
                                                                    <span className="detail-label">{meta.display_key || meta.key}:</span>
                                                                    <span className="detail-value">{displayValue}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Add Order Note */}
                        <div className="card">
                            <div className="card-header">
                                <h3>📝 Add Order Note</h3>
                            </div>
                            <div className="card-content">
                                <div className="note-form">
                                    <div className="form-group">
                                        <label>Note Type:</label>
                                        <select
                                            className="form-control"
                                            value={noteType}
                                            onChange={(e) => setNoteType(e.target.value)}
                                        >
                                            <option value="private">Private Note</option>
                                            <option value="customer">Note to Customer</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            placeholder="Enter your note here..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleAddNote}
                                    >
                                        Add Note
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Sidebar */}
                    <div className="order-sidebar">
                        {/* Order Summary */}
                        <div className="card summary-card">
                            <div className="card-header">
                                <h3>📊 Order Summary</h3>
                            </div>
                            <div className="card-content">
                                <div className="summary-details">
                                    <div className="summary-row">
                                        <span>Order Number:</span>
                                        <strong>#{order.number || order.id}</strong>
                                    </div>
                                    <div className="summary-row">
                                        <span>Order Status:</span>
                                        <select
                                            className="status-select"
                                            value={order.status}
                                            onChange={(e) => handleStatusChange(e.target.value)}
                                            disabled={updating}
                                            style={{
                                                background: statusInfo.bg,
                                                color: statusInfo.color,
                                                border: `1px solid ${statusInfo.color}`
                                            }}
                                        >
                                            {orderStatuses.map(status => (
                                                <option key={status.value} value={status.value}>
                                                    {status.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="summary-row">
                                        <span>Order Date:</span>
                                        <span>{formatDate(order.date_created)}</span>
                                    </div>
                                    <div className="summary-row">
                                        <span>Order Total:</span>
                                        <strong className="total-amount">{formatCurrency(order.total, order.currency)}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Customer Information */}
                        <div className="card">
                            <div className="card-header">
                                <h3>👤 Customer</h3>
                            </div>
                            <div className="card-content">
                                <div className="customer-details">
                                    <div className="customer-name">
                                        {order.billing?.first_name} {order.billing?.last_name}
                                    </div>
                                    {order.billing?.email && (
                                        <div className="customer-contact">
                                            <span className="icon">📧</span>
                                            <a href={`mailto:${order.billing.email}`}>{order.billing.email}</a>
                                        </div>
                                    )}
                                    {order.billing?.phone && (
                                        <div className="customer-contact">
                                            <span className="icon">📞</span>
                                            <a href={`tel:${order.billing.phone}`}>{order.billing.phone}</a>
                                        </div>
                                    )}
                                    <div style={{ marginTop: '12px' }}>
                                        {order.customer_id > 0 ? (
                                            <>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '4px 12px',
                                                    background: '#dbeafe',
                                                    color: '#1e40af',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    marginBottom: '8px'
                                                }}>
                                                    👤 Registered User
                                                </span>
                                                <button
                                                    className="btn btn-secondary btn-sm btn-block"
                                                    onClick={() => navigate(`/customers/edit/${order.customer_id}`)}
                                                >
                                                    View Customer Profile
                                                </button>
                                            </>
                                        ) : (
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '4px 12px',
                                                background: '#f3f4f6',
                                                color: '#6b7280',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                🛒 Guest Order
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Order Timeline */}
                        <div className="card">
                            <div className="card-header">
                                <h3>� Order eTimeline</h3>
                            </div>
                            <div className="card-content">
                                <div className="timeline">
                                    <div className="timeline-item">
                                        <div className="timeline-marker created"></div>
                                        <div className="timeline-content">
                                            <div className="timeline-title">Order Created</div>
                                            <div className="timeline-date">{formatDate(order.date_created)}</div>
                                        </div>
                                    </div>
                                    {order.date_paid && (
                                        <div className="timeline-item">
                                            <div className="timeline-marker paid"></div>
                                            <div className="timeline-content">
                                                <div className="timeline-title">Payment Received</div>
                                                <div className="timeline-date">{formatDate(order.date_paid)}</div>
                                            </div>
                                        </div>
                                    )}
                                    {order.date_completed && (
                                        <div className="timeline-item">
                                            <div className="timeline-marker completed"></div>
                                            <div className="timeline-content">
                                                <div className="timeline-title">Order Completed</div>
                                                <div className="timeline-date">{formatDate(order.date_completed)}</div>
                                            </div>
                                        </div>
                                    )}
                                    {order.date_modified && order.date_modified !== order.date_created && (
                                        <div className="timeline-item">
                                            <div className="timeline-marker modified"></div>
                                            <div className="timeline-content">
                                                <div className="timeline-title">Last Modified</div>
                                                <div className="timeline-date">{formatDate(order.date_modified)}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>


                        {/* Order Information */}
                        <div className="card">
                            <div className="card-header">
                                <h3>ℹ️ Order Information</h3>
                            </div>
                            <div className="card-content">
                                <div className="info-list">
                                    <div className="info-item">
                                        <span className="info-label">Currency:</span>
                                        <span className="info-value">{order.currency}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Items Count:</span>
                                        <span className="info-value">{order.line_items?.length || 0}</span>
                                    </div>
                                    {order.customer_ip_address && (
                                        <div className="info-item">
                                            <span className="info-label">IP Address:</span>
                                            <span className="info-value">{order.customer_ip_address}</span>
                                        </div>
                                    )}
                                    {order.customer_user_agent && (
                                        <div className="info-item">
                                            <span className="info-label">User Agent:</span>
                                            <span className="info-value small">{order.customer_user_agent}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Customer Note */}
                        {order.customer_note && (
                            <div className="card">
                                <div className="card-header">
                                    <h3>💬 Customer Note</h3>
                                </div>
                                <div className="card-content">
                                    <div className="customer-note-box">
                                        {order.customer_note}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Order Notes History */}
                        {orderNotes.length > 0 && (
                            <div className="card">
                                <div className="card-header">
                                    <h3>📋 Order Notes</h3>
                                </div>
                                <div className="card-content">
                                    <div className="notes-list">
                                        {orderNotes.map((note) => (
                                            <div key={note.id} className={`note-item note-${note.type}`}>
                                                <div className="note-header">
                                                    <div className="note-meta">
                                                        <span className="note-type-badge">
                                                            {note.type === 'private' ? '🔒 Private' : '👤 Customer'}
                                                        </span>
                                                        <span className="note-date">{formatDate(note.date)}</span>
                                                    </div>
                                                    {note.type !== 'system' && (
                                                        <div className="note-actions">
                                                            <button
                                                                className="btn-icon"
                                                                onClick={() => handleEditNote(note)}
                                                                title="Edit note"
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="btn-icon"
                                                                onClick={() => handleDeleteNote(note.id)}
                                                                title="Delete note"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                {editingNoteId === note.id ? (
                                                    <div className="note-edit-form">
                                                        <textarea
                                                            className="form-control"
                                                            value={editNoteContent}
                                                            onChange={(e) => setEditNoteContent(e.target.value)}
                                                            rows="3"
                                                        />
                                                        <div className="note-edit-actions">
                                                            <button
                                                                className="btn btn-sm btn-primary"
                                                                onClick={() => handleSaveEditNote(note.id)}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() => {
                                                                    setEditingNoteId(null);
                                                                    setEditNoteContent('');
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="note-content">{note.content}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Refund Dialog */}
                {refundDialogOpen && (
                    <div className="modal-overlay" onClick={() => setRefundDialogOpen(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Process Refund</h3>
                                <button className="modal-close" onClick={() => setRefundDialogOpen(false)}>×</button>
                            </div>
                            <div className="modal-body">
                                <p>Enter the refund amount for order <strong>#{order.number || order.id}</strong>:</p>
                                <div className="form-group">
                                    <label>Refund Amount ({order.currency}):</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        max={order.total}
                                        value={refundAmount}
                                        onChange={(e) => setRefundAmount(e.target.value)}
                                    />
                                    <small className="help-text">Maximum refund: {formatCurrency(order.total, order.currency)}</small>
                                </div>
                                <div className="form-group">
                                    <label>Refund Reason (optional):</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        placeholder="Enter reason for refund..."
                                        value={refundReason}
                                        onChange={(e) => setRefundReason(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setRefundDialogOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleRefund}
                                    disabled={updating}
                                >
                                    {updating ? 'Processing...' : 'Process Refund'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderDetails;
